/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState } from "react";
import { Box } from "@mui/material";
import { Button } from "@chakra-ui/react";
import { FaPlus } from "react-icons/fa";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridClasses,
} from "@mui/x-data-grid";
import AgentAssignSubaccountModal from "./AgentAssignSubaccountModal";
import AgentRevokeSubaccountConfirmModal from "./AgentRevokeSubaccountConfirmModal";
import { Tooltip, IconButton } from "@mui/material";
import SweetAlert2 from "react-sweetalert2";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { MdRemoveCircleOutline, MdOutlineContentCopy } from "react-icons/md";
import { AiOutlineFileSearch } from "react-icons/ai";
import { Link } from "react-router-dom";

dayjs.extend(utc);
dayjs.extend(timezone);

type RowData = {
  id: string;
  name: string;
};

const AgentSubaccountList: React.FC<{
  isLoading: boolean;
  tableData: RowData[];
  page: number;
  pageSize: number;
  rowCount: number;
  setPaginationModel: any;
  currentLastKey: string;
  agentDetail: any;
  assignedSubaccountIds: any;
  triggerReloadList(): void;
}> = ({
  isLoading,
  tableData,
  page,
  pageSize,
  rowCount,
  setPaginationModel,
  currentLastKey,
  agentDetail,
  assignedSubaccountIds,
  triggerReloadList,
}) => {
  const userRole = localStorage.getItem("userRole")?.toLowerCase();

  const [isOpenAssignSubaccountModal, setIsOpenAssignSubaccountModal] =
    useState<boolean>(false);
  const [isOpenRevokeSubaccountModal, setIsOpenRevokeSubaccountModal] =
    useState<boolean>(false);
  const [subaccountDetailToRevoke, setSubaccountDetailToRevoke] =
    useState<any>(null);

  const [swalProps, setSwalProps] = useState<any>({});

  const handleClickCopy = async (id: string) => {
    try {
      await navigator.clipboard.writeText(id);
    } catch (err) {
      console.error("Failed to copy ID: ", err);
    }
  };

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "",
      flex: 1,
      minWidth: 50,
      maxWidth: 50,
      sortable: false,
      headerAlign: "center",
      align: "center",
      renderCell: (params: GridRenderCellParams<RowData, string>) => (
        <Tooltip title="Copy ID" arrow placement="top">
          <div>
            <IconButton
              id={"button-copy-id-" + params?.row?.id}
              onClick={() => handleClickCopy(params.row.id)}
            >
              <MdOutlineContentCopy className="h-5 w-5 text-navy-700" />
            </IconButton>
          </div>
        </Tooltip>
      ),
    },
    {
      field: "suba_name",
      headerName: "Name",
      flex: 1,
      minWidth: 175,
      sortable: false,
      renderCell: (params: GridRenderCellParams<RowData, string>) =>
        params.row.name,
    },
    {
      field: "actions",
      headerName: "",
      sortable: false,
      flex: 1,
      minWidth: 140,
      headerAlign: "right",
      align: "right",
      renderCell: (params) => {
        return (
          <div className="mr-5">
            <Tooltip title="View More Details" arrow placement="top">
              <IconButton
                component={Link}
                to={`/${userRole}/merchants/subaccounts/${params.row.id}`}
                id={"button-detail-" + params?.row?.id}
              >
                <AiOutlineFileSearch className="h-5 w-5 text-navy-700" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Revoke from agent" arrow placement="top">
              <IconButton
                id={"button-revoke-from-agent-" + params?.row?.id}
                onClick={() => handleClickRevokeSubaccount(params.row.id)}
              >
                <MdRemoveCircleOutline className="h-5 w-5 text-red-500 opacity-90" />
              </IconButton>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  const customDataGridStyles: Partial<GridClasses> = {
    root: "mui-table-custom",
    columnHeader: "text-start",
    row: "",
  };

  const showAddAccountModal = () => {
    setIsOpenAssignSubaccountModal(true);
  };

  const closeAddAccountModal = () => {
    setIsOpenAssignSubaccountModal(false);
  };

  const handleClickRevokeSubaccount = (subaccountId: any) => {
    const subaccount = tableData.filter((x: any) => x.id === subaccountId)[0];
    setSubaccountDetailToRevoke(subaccount);
    showRevokeSubaccountModal();
  };

  const showRevokeSubaccountModal = () => {
    setIsOpenRevokeSubaccountModal(true);
  };

  const closeRevokeSubaccountModal = () => {
    setSubaccountDetailToRevoke(null);
    setIsOpenRevokeSubaccountModal(false);
  };

  return (
    <div className="px-4 py-8">
      {isOpenAssignSubaccountModal && agentDetail !== null && (
        <AgentAssignSubaccountModal
          setSwalProps={setSwalProps}
          triggerReloadList={triggerReloadList}
          agentDetail={agentDetail}
          isOpen={isOpenAssignSubaccountModal}
          assignedSubaccountIds={assignedSubaccountIds}
          onClose={closeAddAccountModal}
        />
      )}

      {isOpenRevokeSubaccountModal && subaccountDetailToRevoke && (
        <AgentRevokeSubaccountConfirmModal
          setSwalProps={setSwalProps}
          triggerReloadList={triggerReloadList}
          agentDetail={agentDetail}
          subaccountDetailToRevoke={subaccountDetailToRevoke}
          isOpen={isOpenRevokeSubaccountModal}
          onClose={closeRevokeSubaccountModal}
        />
      )}

      <div className="mb-3 flex items-center justify-end">
        <Button
          isDisabled={isLoading}
          onClick={() => showAddAccountModal()}
          className="btn btn-action !px-6"
        >
          <FaPlus />
          &nbsp;Assign Subaccount
        </Button>
      </div>
      <Box>
        <DataGrid
          sx={{
            "& .MuiDataGrid-cell": {
              padding: "8px 16px",
            },
            "& .MuiDataGrid-columnHeader": {
              padding: "8px 16px",
            },
            "& .MuiTablePagination-input": {
              marginRight: "-20px",
            },
            "& .MuiTablePagination-displayedRows": {
              display: "none",
            },
          }}
          loading={isLoading}
          rows={tableData || []}
          columns={columns}
          getRowId={(row) => row.id}
          rowCount={rowCount}
          sortingMode="server"
          paginationMode="server"
          pageSizeOptions={[10, 25, 50, 100]}
          rowHeight={40}
          columnHeaderHeight={40}
          paginationModel={{ page: page, pageSize: pageSize }}
          onPaginationModelChange={(newValue) => setPaginationModel(newValue)}
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          classes={customDataGridStyles}
          hideFooter={currentLastKey === "" && page === 0}
          autoHeight
          localeText={{
            noRowsLabel: "No data available",
          }}
        />
      </Box>

      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      ></SweetAlert2>
    </div>
  );
};

export default AgentSubaccountList;
